/**
 * Impressão da Ordem de Serviço — folha A4 padronizada para a oficina.
 *
 * Fora do AppShell (sem menu), com o mesmo JWT. Traz tudo o que a oficina e o
 * gestor precisam no papel: empresa, veículo, KM, executor, prazos, a origem
 * (ocorrências do checklist com o problema e as fotos), serviços e peças com
 * totais, itens do plano preventivo atendidos, laudo e assinaturas. O.S. ainda
 * aberta sai com linhas em branco para o serviço executado ser anotado à mão.
 */
import { reasonText } from "../features/maintenance/work-order-checklist-ui";
import { COND_LABEL, INTERVENTION_LABEL, POST_CONDITION, fmtVal } from "../features/maintenance/work-order-inspection-ui";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { MaintenancePlanPlateDto, MaintenanceWorkOrderDetailDto } from "@bi/types";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber } from "../lib/format";
import { useAuth } from "../lib/auth";
import { EvidenceThumbs } from "../features/maintenance/EvidenceThumbs";

const TIPO = { preventive: "Preventiva", corrective: "Corretiva" } as const;
const ORIGEM = { checklist: "Checklist (ocorrência)", manual: "Aberta manualmente", preventive: "Plano preventivo" } as const;
const PRIORIDADE = { low: "Baixa", medium: "Média", high: "Alta" } as const;
const SITUACAO: Record<string, string> = {
  open: "Aberta", scheduled: "Agendada", in_progress: "Em execução", waiting_parts: "Aguardando peças",
  completed: "Concluída", cancelled: "Cancelada",
};
const CRIT = PRIORIDADE;
const dt = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");
const num = (v: string | number | null | undefined) => (v == null ? 0 : Number(v));

export function MaintenanceWorkOrderPrintPage() {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const [emitidaEm] = useState(() => new Date());

  const { data: o, isLoading, error } = useQuery({
    queryKey: ["maintenance", "work-order", id],
    queryFn: () => api.get<MaintenanceWorkOrderDetailDto>(`/maintenance/work-orders/${id}`),
  });
  const empresa = useQuery({
    queryKey: ["companies", "me"],
    queryFn: () => api.get<{ company: { name: string; legalName: string | null; cnpj: string | null } }>("/companies/me"),
    staleTime: Infinity,
  });
  // Rótulos dos itens do plano preventivo que a O.S. atende.
  const plano = useQuery({
    queryKey: ["maintenance", "preventive", "plate", o?.vehiclePlate],
    queryFn: () => api.get<MaintenancePlanPlateDto>(`/maintenance/preventive/plates/${encodeURIComponent(o!.vehiclePlate)}`),
    enabled: !!o && (o.planComponents?.length ?? 0) > 0,
  });

  const pronto = !!o && !empresa.isLoading && (!(o.planComponents?.length) || !plano.isLoading);
  // Abre a janela de impressão sozinha quando tudo carregou (as fotos têm um instante para chegar).
  useEffect(() => {
    if (!pronto) return;
    const t = window.setTimeout(() => window.print(), 1200);
    return () => window.clearTimeout(t);
  }, [pronto]);

  if (isLoading) return <p className="p-8 text-sm text-gray-500">Preparando a O.S. para impressão…</p>;
  if (error || !o) return <p className="p-8 text-sm text-danger-600">Não foi possível carregar a O.S.</p>;

  const fechada = o.status === "completed" || o.status === "cancelled";
  const servicos = o.items.filter((i) => i.kind === "service");
  const pecas = o.items.filter((i) => i.kind === "part");
  const rotuloPlano = (code: string) => plano.data?.rows.find((r) => r.componentCode === code)?.label ?? code;
  const c = empresa.data?.company;

  return (
    <div className="print-os mx-auto max-w-[210mm] bg-white text-gray-900 p-6 print:p-0 text-[12px] leading-snug">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .print-os section { break-inside: avoid; }
        }
      `}</style>

      <div className="no-print mb-4 flex gap-2">
        <button className="btn-primary" onClick={() => window.print()}>Imprimir</button>
        <button className="btn-secondary" onClick={() => window.close()}>Fechar</button>
      </div>

      {/* Cabeçalho */}
      <header className="flex items-start justify-between border-b-2 border-gray-900 pb-2">
        <div>
          <p className="text-base font-bold">{c?.legalName || c?.name || "—"}</p>
          {c?.cnpj && <p>CNPJ {c.cnpj}</p>}
        </div>
        <div className="text-right">
          <p className="text-lg font-bold">ORDEM DE SERVIÇO Nº {o.number}</p>
          <p>{TIPO[o.type]} · {SITUACAO[o.status] ?? o.status}{o.overdue ? " · ATRASADA" : ""}</p>
          <p className="text-gray-500">Emitida em {dt(emitidaEm.toISOString())}{user ? ` por ${user.name}` : ""}</p>
        </div>
      </header>

      {/* Veículo e atendimento */}
      <section className="mt-3 grid grid-cols-4 gap-x-4 gap-y-1.5">
        <Campo r="Placa" v={o.vehiclePlate} forte />
        <Campo r="Implemento" v={o.trailerPlate ?? "—"} />
        <Campo r="KM na abertura" v={o.odometerKm != null ? `${fmtNumber(o.odometerKm)} km` : "—"} />
        <Campo r="Veículo parado" v={o.vehicleStopped ? "Sim" : "Não"} />
        <Campo r="Prioridade" v={PRIORIDADE[o.priority]} />
        <Campo r="Origem" v={ORIGEM[o.origin]} />
        <Campo r="Executor" v={o.executor === "external" ? `Terceiro${o.supplierName ? `: ${o.supplierName}` : ""}` : "Oficina própria"} />
        <Campo r="Responsável" v={o.assignee ?? "—"} />
        <Campo r="Aberta em" v={`${dt(o.openedAt)}${o.createdByName ? ` · ${o.createdByName}` : ""}`} />
        <Campo r="Prazo" v={dt(o.dueAt)} />
        <Campo r="Início da execução" v={dt(o.startedAt)} />
        <Campo r="Fechada em" v={`${dt(o.closedAt)}${o.closedByName ? ` · ${o.closedByName}` : ""}`} />
      </section>

      <section className="mt-3">
        <Titulo>Descrição do serviço solicitado</Titulo>
        <p className="whitespace-pre-wrap">{o.description}</p>
      </section>

      {o.nonConformities.length > 0 && (
        <section className="mt-3">
          <Titulo>Ocorrências de origem (checklist)</Titulo>
          <table className="w-full border-collapse">
            <thead><tr className="text-left border-b border-gray-400"><th className="py-1">Item</th><th>Problema / descrição</th><th className="w-20">Criticidade</th><th className="w-24">Apontada em</th></tr></thead>
            <tbody>
              {o.nonConformities.map((n) => (
                <tr key={n.id} className="align-top border-b border-gray-200">
                  <td className="py-1 pr-2">{n.itemLabel}</td>
                  <td className="py-1 pr-2">
                    {n.defectLabel && <strong>{n.defectLabel}. </strong>}
                    {n.description}
                    <EvidenceThumbs anexos={n.attachments} size="sm" />
                  </td>
                  <td className="py-1">{CRIT[n.criticality]}</td>
                  <td className="py-1">{dt(n.openedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {(o.planComponents?.length ?? 0) > 0 && (
        <section className="mt-3">
          <Titulo>Itens do plano preventivo atendidos</Titulo>
          <ul className="list-disc pl-5">{o.planComponents.map((p) => <li key={p}>{rotuloPlano(p)}</li>)}</ul>
        </section>
      )}

      {o.checklist.items.length > 0 && (
        <section className="mt-3">
          <Titulo>Checklist da preventiva</Titulo>
          <p>
            ✓ {o.checklist.summary.done} realizado{o.checklist.summary.done === 1 ? "" : "s"} · ✕ {o.checklist.summary.notDone} não realizado{o.checklist.summary.notDone === 1 ? "" : "s"} · {o.checklist.summary.notApplicable} não se aplica
            {o.checklist.summary.pending > 0 ? ` · ${o.checklist.summary.pending} pendente${o.checklist.summary.pending === 1 ? "" : "s"}` : ""}
            {" "}(de {o.checklist.summary.total} previstos)
          </p>
          {o.checklist.items.filter((i) => i.status === "not_done").length > 0 && (
            <ul className="list-disc pl-5">
              {o.checklist.items.filter((i) => i.status === "not_done").map((i) => (
                <li key={i.id}>Não realizado: {i.itemName}{i.reason ? ` (${reasonText(i.reason)})` : ""}{i.notes ? ` (${i.notes})` : ""}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {o.technical && o.technical.inspections.length > 0 && (
        <section className="mt-3">
          <Titulo>Execução técnica: o que foi encontrado e o que foi feito</Titulo>
          <table className="w-full border-collapse">
            <thead><tr className="text-left border-b border-gray-400">
              <th className="py-1">Componente / posição</th><th>Medidas</th><th className="w-20">Condição</th><th>Intervenção</th>
            </tr></thead>
            <tbody>
              {o.technical.inspections.map((i) => (
                <tr key={i.id} className="align-top border-b border-gray-200">
                  <td className="py-1 pr-2">{i.itemName}{i.positionLabel ? ` (${i.positionLabel})` : ""}</td>
                  <td className="py-1 pr-2">
                    {(o.technical!.fields[i.checklistItemId] ?? []).filter((f) => i.measures?.[f.name] != null && i.measures[f.name] !== "")
                      .map((f) => `${f.label}: ${fmtVal(i.measures![f.name], f.unit)}`).join(" · ") || "—"}
                    {i.after?.measures && Object.keys(i.after.measures).length > 0 && (
                      <span className="block text-gray-600">Depois: {(o.technical!.fields[i.checklistItemId] ?? []).filter((f) => i.after!.measures![f.name] != null && i.after!.measures![f.name] !== "")
                        .map((f) => `${f.label} ${fmtVal(i.after!.measures![f.name], f.unit)}`).join(" · ")}</span>
                    )}
                  </td>
                  <td className="py-1">{COND_LABEL[i.condition]}</td>
                  <td className="py-1">
                    {i.interventions.length ? i.interventions.map((x) => INTERVENTION_LABEL[x]).join(", ") : "Nenhuma"}
                    {i.pendingReason && <span className="block">Pendente: {i.pendingReason}</span>}
                    {i.notes && <span className="block text-gray-600">{i.notes}</span>}
                    <EvidenceThumbs anexos={i.attachments} size="sm" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {o.technicalReport && (
        <section className="mt-3">
          <Titulo>Resumo técnico</Titulo>
          <p>
            Condição após a manutenção: <strong>{POST_CONDITION[o.technicalReport.postCondition].label}</strong>
            {o.technicalReport.odometerKm != null ? ` · ${fmtNumber(o.technicalReport.odometerKm)} km` : ""} ·
            {" "}{o.technicalReport.counts.inspected} componente(s) inspecionado(s), {o.technicalReport.counts.interventions} intervenção(ões), {o.technicalReport.counts.pending} pendência(s).
          </p>
          {o.technicalReport.interventions.length > 0 && <ul className="list-disc pl-5">{o.technicalReport.interventions.map((x) => <li key={x}>{x}</li>)}</ul>}
          {o.technicalReport.pendings.length > 0 && (
            <><p className="mt-1 font-semibold">Pendências</p><ul className="list-disc pl-5">{o.technicalReport.pendings.map((x) => <li key={x}>{x}</li>)}</ul></>
          )}
          {o.technicalReport.nextPreventive.length > 0 && (
            <p className="mt-1">Próxima preventiva prevista: {o.technicalReport.nextPreventive.map((n) =>
              `${n.label}: ${n.nextKm != null ? `${fmtNumber(n.nextKm)} km` : ""}${n.nextDate ? ` ${new Date(n.nextDate).toLocaleDateString("pt-BR")}` : ""}`).join("; ")}</p>
          )}
        </section>
      )}

      {o.erpLinks.filter((l) => l.status === "active").length > 0 && (
        <section className="mt-3">
          <Titulo>Vínculo com a O.S. do ERP</Titulo>
          {o.erpLinks.filter((l) => l.status === "active").map((l) => (
            <p key={l.id}>
              O.S. ERP #{l.erpOrder?.number ?? l.erpExternalKey}{l.erpOrder ? ` · ${new Date(l.erpOrder.date).toLocaleDateString("pt-BR")}${l.erpOrder.odometer != null ? ` · ${fmtNumber(l.erpOrder.odometer)} km` : ""}` : ""}
              {" "}vinculada por {l.linkedByName ?? "—"} em {dt(l.linkedAt)}{l.matchScore != null ? ` (correspondência ${l.matchScore}%)` : ""}
            </p>
          ))}
        </section>
      )}

      <section className="mt-3">
        <Titulo>Serviços e peças</Titulo>
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-left border-b border-gray-400">
              <th className="py-1 w-16">Tipo</th><th>Descrição</th><th className="text-right w-16">Qtd.</th>
              <th className="text-right w-24">Unitário</th><th className="text-right w-24">Total</th>
            </tr>
          </thead>
          <tbody>
            {[...servicos, ...pecas].map((i) => (
              <tr key={i.id} className="border-b border-gray-200">
                <td className="py-1">{i.kind === "service" ? "Serviço" : "Peça"}</td>
                <td>{i.description}</td>
                <td className="text-right">{num(i.quantity).toLocaleString("pt-BR")}</td>
                <td className="text-right">{fmtBRL(num(i.unitCost))}</td>
                <td className="text-right">{fmtBRL(num(i.totalCost))}</td>
              </tr>
            ))}
            {/* O.S. em aberto: linhas para anotar à mão na oficina. */}
            {!fechada && Array.from({ length: Math.max(4, 8 - o.items.length) }, (_, k) => (
              <tr key={`vazio-${k}`} className="border-b border-gray-200 h-7"><td /><td /><td /><td /><td /></tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={4} className="text-right pt-1">Serviços</td><td className="text-right pt-1">{fmtBRL(o.totals.services)}</td></tr>
            <tr><td colSpan={4} className="text-right">Peças</td><td className="text-right">{fmtBRL(o.totals.parts)}</td></tr>
            <tr className="font-bold">
              <td colSpan={4} className="text-right">Total</td>
              <td className="text-right">{fmtBRL(o.items.length ? o.totals.total : num(o.cost))}</td>
            </tr>
          </tfoot>
        </table>
        {!o.items.length && o.cost != null && <p className="text-gray-500 mt-1">Custo informado na O.S., sem itens detalhados.</p>}
      </section>

      <section className="mt-3">
        <Titulo>{o.status === "cancelled" ? "Motivo do cancelamento" : "Laudo / serviço executado"}</Titulo>
        {o.status === "cancelled" ? <p>{o.cancelReason ?? "—"}</p>
          : o.resolution ? <p className="whitespace-pre-wrap">{o.resolution}</p>
            : <div className="space-y-5 pt-2">{[0, 1, 2, 3].map((k) => <div key={k} className="border-b border-gray-400" />)}</div>}
        {!fechada && (
          <p className="mt-3">KM na conclusão: ____________________ &nbsp;&nbsp; Data/hora: ____/____/______ ____:____</p>
        )}
      </section>

      {o.notes && (
        <section className="mt-3">
          <Titulo>Observações</Titulo>
          <p className="whitespace-pre-wrap">{o.notes}</p>
        </section>
      )}

      {o.events.length > 0 && (
        <section className="mt-3">
          <Titulo>Histórico</Titulo>
          <ul className="text-[11px] text-gray-700">
            {o.events.map((e) => (
              <li key={e.id}>
                {dt(e.createdAt)}, {e.createdByName ?? "sistema"}:{" "}
                {e.type === "created" ? e.message ?? "O.S. aberta."
                  : `${e.toStatus ? `${SITUACAO[e.fromStatus ?? ""] ?? "—"} → ${SITUACAO[e.toStatus] ?? e.toStatus}. ` : ""}${e.message ?? ""}`}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-10 grid grid-cols-3 gap-8 text-center">
        {["Executor / oficina", "Motorista", "Gestor da frota"].map((a) => (
          <div key={a}><div className="border-t border-gray-900 pt-1">{a}</div></div>
        ))}
      </section>
    </div>
  );
}

function Titulo({ children }: { children: React.ReactNode }) {
  return <h2 className="text-[11px] font-bold uppercase tracking-wide border-b border-gray-400 mb-1">{children}</h2>;
}

function Campo({ r, v, forte }: { r: string; v: string; forte?: boolean }) {
  return (
    <div>
      <p className="text-[10px] uppercase text-gray-500">{r}</p>
      <p className={forte ? "font-bold text-sm" : ""}>{v}</p>
    </div>
  );
}
